import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useDropZone } from "../lib/useDropZone";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FolderArchive, Upload, Repeat } from "lucide-react";
import { STATO_ATTIVITA } from "../components/FornitoreFascicolo";

export default function DocumentiFornitore() {
  const [rows, setRows] = useState(null);
  const uploadRef = useRef({});

  const load = () => api.get("/ras/mie-attivita").then((r) => setRows(r.data.attivita)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (doc, fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    const fd = new FormData();
    arr.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/ras/mie-attivita/${doc.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} document${data.count === 1 ? "o inviato" : "i inviati"} allo Studio. Grazie!`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="documenti-fornitore-page">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
        <FolderArchive className="w-9 h-9 text-brand" /> Documenti richiesti
      </h1>
      <p className="text-slate-600">
        Le attività periodiche che lo Studio ti ha affidato. Al termine di ogni attività carica qui la
        documentazione aggiornata (verbale, rapporto, lettura…): la scadenza si rinnoverà automaticamente.
      </p>

      {!rows && <div className="text-slate-500">Caricamento…</div>}
      {rows && rows.length === 0 && (
        <Card className="p-8 text-center text-slate-500 border-2" data-testid="documenti-fornitore-vuoto">
          Nessuna attività periodica assegnata al momento.
        </Card>
      )}

      {(rows || []).map((d) => (
        <FornitoreDocCard key={d.id} d={d} onUpload={onUpload} uploadRef={uploadRef} />
      ))}
    </div>
  );
}

function FornitoreDocCard({ d, onUpload, uploadRef }) {
  const st = STATO_ATTIVITA[d.stato] || STATO_ATTIVITA.senza_scadenza;
  const { dragProps, isDragOver } = useDropZone((files) => onUpload(d, files));
  return (
    <Card {...dragProps} className={`p-4 border-2 relative transition-all ${isDragOver ? "border-brand bg-brand/5 shadow-md" : ""}`} data-testid={`mia-attivita-${d.id}`}>
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
          <div className="flex items-center gap-2 text-brand font-bold text-sm bg-white/95 px-3 py-1.5 rounded-full border-2 border-brand shadow-md">
            <Upload className="w-4 h-4" /> Rilascia per caricare
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-bold break-words">{d.documento}</div>
          <div className="text-sm text-slate-500">{d.condominio_nome}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded ${st.cls}`}>
          <st.Icon className="w-3.5 h-3.5" /> {st.label}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-600">
        {d.ricorrenza_mesi ? <span className="inline-flex items-center gap-1"><Repeat className="w-4 h-4" /> ogni {d.ricorrenza_mesi} mesi</span> : null}
        {d.data_scadenza && <span>Scadenza: {d.data_scadenza}</span>}
        {(d.files || []).length > 0 && <span className="text-green-700">{d.files.length} file caricat{d.files.length === 1 ? "o" : "i"}</span>}
      </div>
      {(d.files || []).length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`mia-attivita-files-${d.id}`}>
          {d.files.map((f) => (
            <div key={f.file_id} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1.5 break-all">
              <span className="text-brand">📎</span> <span className="truncate">{f.file_name}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3">
        <input type="file" hidden multiple ref={(el) => (uploadRef.current[d.id] = el)}
          onChange={(e) => { onUpload(d, e.target.files); e.target.value = ""; }} accept=".pdf,image/*,.doc,.docx" />
        <Button className="h-10 bg-brand hover:bg-brand-dark font-bold" onClick={() => uploadRef.current[d.id]?.click()}
          data-testid={`mia-attivita-upload-${d.id}`}>
          <Upload className="w-4 h-4 mr-2" /> Carica documenti (anche più di uno)
        </Button>
        <p className="text-xs text-slate-500 mt-1.5 italic">Suggerimento: puoi trascinare i file direttamente sulla card.</p>
      </div>
    </Card>
  );
}
